// ============================================================
// NEBULA FINANCE AI — Activity Logger
// E:\Nebula\nebula-investment\src\utils\activityLogger.js
//
// Drop into your existing src/utils folder.
// Uses your existing supabase client — no extra config.
// ============================================================

import { supabase } from '../supabaseClient'; // adjust if your path differs

// ── Session ID (unique per browser tab session) ───────────────
let _sessionId = sessionStorage.getItem('nebula_session_id');
if (!_sessionId) {
  _sessionId = crypto.randomUUID();
  sessionStorage.setItem('nebula_session_id', _sessionId);
}
export const SESSION_ID = _sessionId;

// ── IP (fetched once, then cached) ────────────────────────────
let _cachedIp = null;
let _cachedLocation = null;
async function getClientMeta() {
  if (_cachedIp) return { ip: _cachedIp, location: _cachedLocation };
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    _cachedIp = data.ip || 'unknown';
    _cachedLocation = `${data.city}, ${data.country_name}`;
  } catch {
    _cachedIp = 'unknown';
    _cachedLocation = 'unknown';
  }
  return { ip: _cachedIp, location: _cachedLocation };
}

// ── Core logger ───────────────────────────────────────────────
export async function logActivity({ eventType, page = null, actionLabel = null, actionType = null }) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { ip, location } = await getClientMeta();

    // 1. Permanent event log
    await supabase.from('activity_events').insert({
      user_id:      user.id,
      user_email:   user.email,
      user_name:    user.user_metadata?.full_name || user.email,
      company_name: user.user_metadata?.company_name || null,
      plan_type:    user.user_metadata?.plan_type || 'demo',
      event_type:   eventType,
      page,
      action_label: actionLabel,
      action_type:  actionType,
      ip_address:   ip,
      location:     location,
      user_agent:   navigator.userAgent,
      session_id:   SESSION_ID,
    });

    // 2. Live session state
    if (eventType === 'logout') {
      await supabase.from('active_sessions').delete().eq('session_id', SESSION_ID);
    } else {
      await supabase.from('active_sessions').upsert({
        session_id:     SESSION_ID,
        user_id:        user.id,
        user_email:     user.email,
        user_name:      user.user_metadata?.full_name || user.email,
        company_name:   user.user_metadata?.company_name || null,
        plan_type:      user.user_metadata?.plan_type || 'demo',
        ip_address:     ip,
        location:       location,
        current_page:   page || 'Dashboard',
        current_action: actionLabel,
        action_type:    actionType,
        last_seen:      new Date().toISOString(),
        ...(eventType === 'login' ? { login_time: new Date().toISOString() } : {}),
      }, { onConflict: 'session_id' });
    }
  } catch (err) {
    console.warn('[ActivityLogger]', err.message); // silent — never blocks user
  }
}

// ── Convenience wrappers (use these directly in your components) ──
export const logLogin            = ()               => logActivity({ eventType:'login',      page:'Dashboard',       actionLabel:'Logged in',              actionType:'navigation' });
export const logLogout = async () => {
  try {
    await supabase.from('active_sessions').delete().eq('session_id', SESSION_ID);
    await supabase.from('activity_events').insert({
      event_type:   'logout',
      action_label: 'Logged out',
      action_type:  'navigation',
      session_id:   SESSION_ID,
      created_at:   new Date().toISOString(),
    });
  } catch (err) {
    console.warn('[ActivityLogger] logout:', err.message);
  }
};
export const logPageView         = (page)           => logActivity({ eventType:'navigate',   page,                   actionLabel:`Navigated to ${page}`,   actionType:'navigation' });
export const logExcelUpload      = (page, fileName) => logActivity({ eventType:'upload',     page,                   actionLabel:`Uploaded: ${fileName}`,  actionType:'upload'     });
export const logAiRun            = (page, feature)  => logActivity({ eventType:'ai_run',     page,                   actionLabel:feature,                  actionType:'ai'         });
export const logReportGeneration = (page, name)     => logActivity({ eventType:'report',     page,                   actionLabel:`Generated: ${name}`,     actionType:'report'     });
export const logDataInput        = (page, action)   => logActivity({ eventType:'data_input', page,                   actionLabel:action,                   actionType:'data'       });

// ============================================================
// WHERE TO ADD EACH CALL:
//
// LOGIN:
//   import { logLogin } from '../utils/activityLogger';
//   // after signInWithPassword resolves:
//   logLogin();
//
// LOGOUT:
//   import { logLogout } from '../utils/activityLogger';
//   logLogout();  // before signOut()
//
// PAGE NAVIGATION (App.jsx, inside router):
//   import { useEffect } from 'react';
//   import { useLocation } from 'react-router-dom';
//   import { logPageView } from '../utils/activityLogger';
//   const PAGE_NAMES = {
//     '/':                    'Dashboard',
//     '/market-intelligence': 'Market Intelligence',
//     '/hedge-solutions':     'Hedge Solutions',
//     '/investments':         'Investments',
//     '/debt-management':     'Debt Management',
//     '/receivables':         'Receivables',
//     '/payables':            'Payables',
//   };
//   const location = useLocation();
//   useEffect(() => {
//     logPageView(PAGE_NAMES[location.pathname] || location.pathname);
//   }, [location.pathname]);
//
// AI RUNS:
//   logAiRun('Market Intelligence', 'Running AI Insights');
//   logAiRun('Hedge Solutions', 'Running Monte Carlo Simulation');
//   logAiRun('Receivables', 'Running Natural Hedge Analysis');
//
// EXCEL UPLOAD:
//   logExcelUpload('Receivables', file.name);
//
// REPORT:
//   logReportGeneration('Investments', 'Portfolio Summary Report');
//
// DATA INPUT:
//   logDataInput('Payables', 'Added new payable entry');
// ============================================================
